/**
 * Pure (site, esys) resolution for the match-to-sites modal.
 *
 * Extracted from MatchToSitesModal so it can be unit-tested without importing the component's
 * runtime dependency chain. Only TYPES are imported from the api-client (erased at compile), so
 * this module has no heavy transitive imports.
 */
import type {
  MatchCandidate,
  MatchResult,
} from "@inrange/building-manager-api-client";

export interface PerRowDecision {
  // Which candidate the operator selected; null = rejected, undefined = no decision yet
  selectedSiteId: string | null | undefined;
  // For multi-esys sites, the chosen esys key (defaults to "0" when there's only one)
  selectedEsys: string;
  // Whether the operator typed in a free-text "other site" override
  overrideSiteId: string | null;
}

export type OrgSite = { id: string; name: string; esys: string[] };

// Committing two files onto the same (site, esys) would silently overwrite one with the other
// (the second hhDemandGuid wins), so a (site, esys) target may be claimed by at most one file.
export const mappingKey = (siteId: string, esys: string) => `${siteId}::${esys}`;

export const esysForSite = (
  siteId: string,
  orgSites: OrgSite[],
  fallback?: string[]
): string[] => orgSites.find((s) => s.id === siteId)?.esys ?? fallback ?? ["0"];

// The order to try a candidate's esys in: the MPAN-matched esys first (so a per-meter curve
// lands on the esys whose grid connection is ITS MPAN, not a positional first-free that could
// cross-assign two curves of the same site), then the rest. When the site's esys carry no MPAN
// (matched_esys_keys empty - the common initial state) this is just the site's esys order, i.e.
// the previous first-free behaviour. Callers then pick the first entry that isn't already taken.
export const preferredEsysOrder = (
  c: MatchCandidate,
  orgSites: OrgSite[]
): string[] => {
  const all = esysForSite(c.site_id, orgSites, c.esys_keys);
  const matched = (c.matched_esys_keys ?? []).filter((e) => all.includes(e));
  return [...matched, ...all.filter((e) => !matched.includes(e))];
};

// The (site, esys) target a decision resolves to, or null when the row is rejected/undecided.
export const decisionKey = (dec: PerRowDecision | undefined): string | null => {
  const siteId = dec?.overrideSiteId ?? dec?.selectedSiteId;
  if (!siteId) return null;
  return mappingKey(siteId, dec?.selectedEsys ?? "0");
};

// Files whose best candidate is below this confidence default to "Reject - skip this file":
// the matcher isn't confident enough to pre-pick a site, so the operator opts in explicitly.
export const DEFAULT_REJECT_BELOW_CONFIDENCE = 50;

// Default-select per row, greedily avoiding (site, esys) collisions. Rows are processed in
// confidence order so the strongest match claims a contested target; a row whose every
// candidate's (site, esys) is already taken is left undecided for the operator to resolve.
export const computeDefaultDecisions = (
  matches: MatchResult[],
  orgSites: OrgSite[]
): Record<string, PerRowDecision> => {
  const claimed = new Set<string>();
  const result: Record<string, PerRowDecision> = {};
  const ordered = [...matches].sort(
    (a, b) =>
      (b.candidates[0]?.confidence ?? 0) - (a.candidates[0]?.confidence ?? 0)
  );
  const reject: PerRowDecision = {
    selectedSiteId: null,
    selectedEsys: "0",
    overrideSiteId: null,
  };
  for (const m of ordered) {
    // Low-confidence files default to reject rather than auto-picking a shaky match.
    if ((m.candidates[0]?.confidence ?? 0) < DEFAULT_REJECT_BELOW_CONFIDENCE) {
      result[m.file_guid] = reject;
      continue;
    }
    let decision: PerRowDecision | null = null;
    for (const c of m.candidates) {
      const freeEsys = preferredEsysOrder(c, orgSites).find(
        (e) => !claimed.has(mappingKey(c.site_id, e))
      );
      if (freeEsys) {
        decision = {
          selectedSiteId: c.site_id,
          selectedEsys: freeEsys,
          overrideSiteId: null,
        };
        claimed.add(mappingKey(c.site_id, freeEsys));
        break;
      }
    }
    result[m.file_guid] = decision ?? reject;
  }
  return result;
};
